import axios from '@/plugins/axios';
import ErrorHandle from '@/helpers/errors/errorHandle';
import enumerateArray from '@/helpers/enumerateArray';
import downloadFile from '@/helpers/downloadFile';

const initialState = {
  investorsListData: {},
  investorsStatuses: [],
  investorStatus: null,
  investorsLocations: [],
  investorAutosData: {},
};
const getters = {
  getInvestorsListData: (state) => state.investorsListData,
  getInvestorsStatuses: (state) => state.investorsStatuses,
  getInvestorStatus: (state) => state.investorStatus,
  getInvestorsLocations: (state) => state.investorsLocations,
  getInvestorAutosData: (state) => state.investorAutosData,
};

const mutations = {
  setInvestorsListData(state, data) {
    state.investorsListData = data;
  },
  setInvestorsStatuses(state, data) {
    state.investorsStatuses = data;
  },
  setInvestorStatus(state, data) {
    state.investorStatus = data;
  },
  setInvestorsLocations(state, data) {
    state.investorsLocations = data;
  },
  setInvestorAutosData(state, data) {
    state.investorAutosData = data;
  },
};

/* eslint-disable no-unused-vars */
const actions = {
  async fetchInvestorsListData({ commit }, credentials) {
    try {
      commit('global/setLoader', true, { root: true });
      const token = localStorage.getItem('token') || '';
      const lang = localStorage.getItem('language') || '';
      const searchString = credentials.searchString
        ? encodeURIComponent(credentials.searchString)
        : '';
      const page = credentials.page || '';
      const investorStatus = credentials.investorStatus || '';
      const investorLocation = credentials.investorLocation || '';

      const data = await axios.get(
        `/investors?page=${page}&searchString=${searchString}&status=${investorStatus}&location=${investorLocation}`,
        { headers: { Authorization: `Bearer ${token}`, Lang: lang } },
      );

      const investorsListData = {
        ...data.data.data.investors,
        items: enumerateArray(
          data.data.data.investors.items,
          data.data.data.investors.currentPage,
          data.data.data.investors.perPage,
        ),
      };

      commit('setInvestorsListData', investorsListData);
      commit('global/setLoader', false, { root: true });
      return investorsListData;
    } catch (error) {
      commit('global/setLoader', false, { root: true });
      throw ErrorHandle(error, this);
    }
  },
  async downloadList(_, { params, name }) {
    try {
      const lang = localStorage.getItem('language') || '';
      const token = localStorage.getItem('token') || '';
      const { data } = await axios.get('investors', {
        responseType: 'blob',
        headers: { Authorization: `Bearer ${token}`, Lang: lang },
        params: {
          ...params,
          export: 1,
        },
      });

      downloadFile(data, name);

      return { success: true };
    } catch {
      return { error: true };
    }
  },
  async fetchInvestorsProperties({ commit }) {
    try {
      commit('global/setLoader', true, { root: true });
      const token = localStorage.getItem('token') || '';
      const lang = localStorage.getItem('language') || '';
      const data = await axios.get('/investors/properties', {
        headers: { Authorization: `Bearer ${token}`, Lang: lang },
      });

      const { status, location } = data.data.data.properties;

      commit('setInvestorsStatuses', status);
      commit('setInvestorsLocations', location);
      commit('setInvestorStatus', status[0]);
      return data;
    } catch (error) {
      commit('global/setLoader', false, { root: true });
      throw ErrorHandle(error, this);
    }
  },
  async fetchInvestorAutosData({ commit }, credentials) {
    try {
      commit('global/setLoader', true, { root: true });
      const token = localStorage.getItem('token') || '';
      const lang = localStorage.getItem('language') || '';
      const page = credentials.page ? credentials.page : '';
      const investorId = credentials.investorId ? credentials.investorId : '';
      const data = await axios.get(
        `/investors/${investorId}/cars/?page=${page}`,
        { headers: { Authorization: `Bearer ${token}`, Lang: lang } },
      );

      const investorAutosData = {
        ...data.data.data.cars,
        items: enumerateArray(
          data.data.data.cars.items,
          data.data.data.cars.currentPage,
          data.data.data.cars.perPage,
        ),
      };

      commit('setInvestorAutosData', investorAutosData);
      commit('global/setLoader', false, { root: true });
      return investorAutosData;
    } catch (error) {
      commit('global/setLoader', false, { root: true });
      throw ErrorHandle(error, this);
    }
  },
  /* eslint-disable no-unused-vars */
  async createInvestor({ commit }, investorData) {
    try {
      const token = localStorage.getItem('token') || '';
      const lang = localStorage.getItem('language') || '';
      const data = await axios.post('/investors', investorData, {
        headers: { Authorization: `Bearer ${token}`, Lang: lang },
      });

      return data.data;
    } catch (error) {
      throw ErrorHandle(error, this);
    }
  },
  async fetchInvestorData({ commit }, investorId) {
    try {
      const token = localStorage.getItem('token') || '';
      const lang = localStorage.getItem('language') || '';
      const data = await axios.get(`/investors/${investorId}`, {
        headers: { Authorization: `Bearer ${token}`, Lang: lang },
      });
      return data.data;
    } catch (error) {
      throw ErrorHandle(error, this);
    }
  },
  async editInvestor({ commit }, investorData) {
    try {
      const { investorId } = investorData;
      const token = localStorage.getItem('token') || '';
      const lang = localStorage.getItem('language') || '';
      const data = await axios.put(
        `/investors/${investorId}`,
        investorData.data,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            Lang: lang,
          },
        },
      );

      return data.data;
    } catch (error) {
      throw ErrorHandle(error, this);
    }
  },
  async changeStatus({ commit }, investorId) {
    try {
      const token = localStorage.getItem('token') || '';
      const lang = localStorage.getItem('language') || '';
      const data = await axios.patch(`/investors/${investorId}/status`, null, {
        headers: {
          Authorization: `Bearer ${token}`,
          Lang: lang,
        },
      });
      return data.data;
    } catch (error) {
      throw ErrorHandle(error, this);
    }
  },
};

export default {
  namespaced: true,
  state: initialState,
  getters,
  mutations,
  actions,
};
